import Deps from './deps';
import { Amplify, Hub } from '@aws-amplify/core';
import { Auth } from '@aws-amplify/auth';
import React from 'react';
import {
  redirectAfterSessionLoss,
  startLogoutMarkerMonitor,
} from '@seller-hermes/utils/auth-session-expiry';
import {
  migrateCognitoSessionToCookies,
  purgeLegacyCognitoStorage,
} from '@seller-hermes/utils/cognito-storage-migration';

const {
  NX_COGNITO_USER_POOL_ID: userPoolId,
  NX_COGNITO_REGION: region,
  NX_COGNITO_USER_POOL_WEB_CLIENT_ID: userPoolWebClientId,
  NX_ENABLE_COGNITO_COOKIE_STORAGE: enableCognitoCookieStorage,
  STORYBOOK: isStorybook,
} = process.env;

const canUseSharedCookieDomain =
  !isStorybook &&
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith('.abbiamolog.com');
const shouldUseCognitoCookieStorage =
  enableCognitoCookieStorage === 'true' && canUseSharedCookieDomain;

// ORDEM CRÍTICA: a migração precisa terminar ANTES do Amplify.configure abaixo.
// Se rodar depois (ou virar import com side-effect), o Amplify inicializa
// lendo um cookieStorage ainda vazio e todo cliente vindo do domínio legado
// — que rodava com o storage default, localStorage — é deslogado no primeiro
// load após a virada. Chamada explícita, e não import, justamente para que a
// ordem fique visível em code review.
if (shouldUseCognitoCookieStorage) {
  const migrationResult = migrateCognitoSessionToCookies();

  // Diagnóstico da virada. O shim decide entre 8 caminhos e, sem isso, uma
  // sessão que não sobrevive é indistinguível de outra: não dá pra separar
  // "não achou sessão no localStorage" de "bloqueou por logout" de "preservou
  // cookie canônico". O valor é um enum, sem token e sem PII.
  // Remover quando o endpoint de telemetria existir.
  (window as unknown as Record<string, unknown>)['__abbiamoSessionMigration'] =
    migrationResult;
  console.info('[abbiamo] migração de sessão:', migrationResult);
} else {
  // Gate desligado é indistinguível de "rodou e não fez nada" se ficar mudo —
  // e é a diferença entre config errada e defeito no shim.
  const reason =
    enableCognitoCookieStorage !== 'true'
      ? `NX_ENABLE_COGNITO_COOKIE_STORAGE=${String(enableCognitoCookieStorage)}`
      : 'host fora de .abbiamolog.com';

  (window as unknown as Record<string, unknown>)['__abbiamoSessionMigration'] =
    `disabled:${reason}`;
  console.info('[abbiamo] migração de sessão desabilitada:', reason);
}

const awsConfig = {
  Auth: {
    userPoolId,
    region,
    userPoolWebClientId,
    mandatorySignIn: true,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    ...(shouldUseCognitoCookieStorage
      ? {
          cookieStorage: {
            domain: '.abbiamolog.com',
            secure: true,
            path: '/',
            sameSite: 'lax',
            expires: 30,
          },
        }
      : {}),
  },
};

Amplify.configure(awsConfig);
Auth.configure(awsConfig);

// O redirect do domínio legado para o portal NÃO mora aqui — está no loader de
// rota (routes.tsx), depois do start(). Em escopo de módulo o store ainda não
// reidratou e o /me não rodou, então o seller group em que o usuário está
// seria desconhecido, e é ele que o portal precisa receber para encaminhar o
// usuário ao ambiente certo em vez de mostrar o seletor.

Hub.listen('auth', ({ payload: { event } }) => {
  // Logout deliberado: limpa o rastro do localStorage para que o shim não
  // ressuscite a sessão no próximo load do domínio legado.
  if (event === 'signOut') {
    purgeLegacyCognitoStorage();
    redirectAfterSessionLoss();
    return;
  }

  // Pode ser blip de rede: redireciona, mas preserva o localStorage.
  if (event === 'tokenRefresh_failure') {
    redirectAfterSessionLoss();
  }
});

startLogoutMarkerMonitor();

const App = () => {
  return <Deps />;
};

export default App;
